import { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Modal — structural shell atom.
 *
 * Renders overlay + panel + slots. Supplies no content.
 * Callers compose header/body/footer via the named slot props
 * or via children (body-only shorthand).
 *
 * Props:
 *   isOpen     boolean
 *   onClose    () => void
 *   size       'sm' | 'md' | 'lg'   (default 'md')
 *   title      ReactNode — rendered in modal__title
 *   footer     ReactNode — rendered in modal__footer (optional)
 *   children   ReactNode — rendered in modal__body
 */
const Modal = ({ isOpen, onClose, size, title, footer, children }) => {
  const closeRef = useRef(null);

  // Focus close button on open
  useEffect(() => {
    if (isOpen) closeRef.current?.focus();
  }, [isOpen]);

  // Escape key
  const handleKeyDown = useCallback(
    (e) => { if (e.key === 'Escape') onClose(); },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const sizeClass = size === 'sm' ? 'modal-sm' : size === 'lg' ? 'modal-lg' : '';

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className={`modal ${sizeClass}`}>

        {/* Header — always rendered; title is required for a11y */}
        <div className="modal__header">
          <div className="modal__title">
            {title}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            {/* Inline SVG — no icon dependency */}
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M14 4L4 14M4 4l10 10" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="modal__body">
          {children}
        </div>

        {/* Footer — optional */}
        {footer && (
          <div className="modal__footer">
            {footer}
          </div>
        )}

      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen:   PropTypes.bool.isRequired,
  onClose:  PropTypes.func.isRequired,
  size:     PropTypes.oneOf(['sm', 'md', 'lg']),
  title:    PropTypes.node.isRequired,
  footer:   PropTypes.node,
  children: PropTypes.node,
};

Modal.defaultProps = {
  size:     'md',
  footer:   null,
  children: null,
};

export default Modal;